import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { MANENO_TYPE, MANENO_RADIUS, MANENO_SHADOW, MANENO_SPACE } from "../maneno/manenoTheme";

interface SkinProps {
  $isGriffle?: boolean;
}

const GRIFFLE = {
  primary: "#539a18",
  dark: "#1b3a0a",
  border: "#d4dfd0",
  surface: "#ffffff",
  ink: "#1a1a1a",
  inkMuted: "#52525b",
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  radius: "10px",
  radiusSm: "8px"
};

/* ── Quick Actions ─────────────────────────────────────────── */

export const StyledQuickActions = styled.div<SkinProps>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${MANENO_SPACE.cardGap};
  margin: 20px 0;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledQuickActionCard = styled(Link)<SkinProps>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: var(--color-surface);
  border: 1px solid ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.border : "var(--color-border)")};
  border-radius: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.radius : MANENO_RADIUS.card)};
  text-decoration: none;
  color: inherit;
  box-shadow: ${({ $isGriffle }) => ($isGriffle ? "none" : MANENO_SHADOW.card)};
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    text-decoration: none;
    color: inherit;

    ${({ $isGriffle }) =>
      $isGriffle
        ? css`
            box-shadow: 0 4px 16px rgba(83, 154, 24, 0.12);
            border-color: ${GRIFFLE.primary};
          `
        : css`
            box-shadow: ${MANENO_SHADOW.cardHover};
            border-color: var(--color-primary);
          `}
  }

  .action-icon {
    width: 36px;
    height: 36px;
    border-radius: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.radiusSm : MANENO_RADIUS.cardSm)};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    ${({ $isGriffle }) =>
      $isGriffle
        ? css`
            background: #e8f5d6;
            color: ${GRIFFLE.primary};
          `
        : css`
            background: var(--color-primary-light);
            color: var(--color-primary);
          `}

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .action-title {
    font-family: var(--font-body);
    font-weight: ${MANENO_TYPE.weightSemi};
    font-size: ${({ $isGriffle }) => ($isGriffle ? "14px" : MANENO_TYPE.body)};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.ink : "var(--color-ink)")};
    margin: 0;
    line-height: 1.3;
  }

  .action-description {
    font-size: ${MANENO_TYPE.bodySmall};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.inkMuted : "var(--color-ink-muted)")};
    margin: 0;
    line-height: 1.4;
  }

  .action-count {
    font-weight: ${MANENO_TYPE.weightBold};
    font-size: 20px;
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.primary : "var(--color-primary)")};
    line-height: 1;
  }
`;

/* ── Activity Feed ─────────────────────────────────────────── */

export const StyledActivityFeed = styled.div<SkinProps>`
  background: var(--color-surface);
  border: 1px solid ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.border : "var(--color-border)")};
  border-radius: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.radius : MANENO_RADIUS.card)};
  box-shadow: ${({ $isGriffle }) => ($isGriffle ? "none" : MANENO_SHADOW.card)};
  padding: 20px;
  margin: 20px 0;

  .feed-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .feed-title {
    font-weight: ${MANENO_TYPE.weightSemi};
    font-size: ${({ $isGriffle }) => ($isGriffle ? "18px" : MANENO_TYPE.sectionTitle)};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.ink : "var(--color-ink)")};
    margin: 0;
  }

  .feed-view-all {
    font-size: ${MANENO_TYPE.body};
    font-weight: ${MANENO_TYPE.weightMd};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.primary : "var(--color-primary)")};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .feed-list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: 420px;
    overflow-y: auto;
  }

  .feed-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.border : "var(--color-border-light)")};

    &:last-child {
      border-bottom: none;
    }
  }

  .feed-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${MANENO_TYPE.weightSemi};
    font-size: ${MANENO_TYPE.xsmall};
    color: #ffffff;
    background: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.primary : "var(--color-primary)")};
  }

  .feed-content {
    flex: 1;
    min-width: 0;
  }

  .feed-student-name {
    font-weight: ${MANENO_TYPE.weightMd};
    font-size: ${MANENO_TYPE.body};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.ink : "var(--color-ink)")};
  }

  .feed-action {
    font-size: ${MANENO_TYPE.bodySmall};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.inkMuted : "var(--color-ink-muted)")};
    margin: 2px 0 0;
  }

  .feed-timestamp {
    font-size: ${MANENO_TYPE.xsmall};
    color: var(--color-ink-faint);
    white-space: nowrap;
    flex-shrink: 0;
    padding-top: 2px;
  }
`;

/* ── Class At A Glance ─────────────────────────────────────── */

export const StyledClassGlance = styled.div<SkinProps>`
  margin: 20px 0;

  .glance-title {
    font-weight: ${MANENO_TYPE.weightSemi};
    font-size: ${({ $isGriffle }) => ($isGriffle ? "18px" : MANENO_TYPE.sectionTitle)};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.ink : "var(--color-ink)")};
    margin: 0 0 12px;
  }

  .glance-grid {
    display: flex;
    gap: ${MANENO_SPACE.cardGap};
    overflow-x: auto;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.border : "var(--color-border)")};
      border-radius: 3px;
    }
  }
`;

export const StyledClassCard = styled.button<SkinProps>`
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: var(--color-surface);
  border: 1px solid ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.border : "var(--color-border)")};
  border-radius: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.radius : MANENO_RADIUS.card)};
  box-shadow: ${({ $isGriffle }) => ($isGriffle ? "none" : MANENO_SHADOW.card)};
  cursor: pointer;
  text-align: left;
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;

  &:hover {
    transform: translateY(-2px);

    ${({ $isGriffle }) =>
      $isGriffle
        ? css`
            box-shadow: 0 4px 16px rgba(83, 154, 24, 0.12);
            border-color: ${GRIFFLE.primary};
          `
        : css`
            box-shadow: ${MANENO_SHADOW.cardHover};
            border-color: var(--color-primary);
          `}
  }

  .class-name {
    font-weight: ${MANENO_TYPE.weightSemi};
    font-size: ${({ $isGriffle }) => ($isGriffle ? "15px" : MANENO_TYPE.cardTitle)};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.ink : "var(--color-ink)")};
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .class-stat {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: ${MANENO_TYPE.bodySmall};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.inkMuted : "var(--color-ink-muted)")};
  }

  .class-stat-value {
    font-weight: ${MANENO_TYPE.weightBold};
    color: ${({ $isGriffle }) => ($isGriffle ? GRIFFLE.primary : "var(--color-primary)")};
  }
`;
